<template>
  <div ref="splitContainer" class="flex w-full h-full relative select-none overflow-hidden">
    <!-- Left pane -->
    <div
      ref="leftPane"
      class="overflow-hidden h-full transition-all duration-300 ease-in-out"
      :style="{ width: leftWidth + 'px' }"
    >
      <div ref="leftWrapper" class="h-full">
        <slot name="left" />
      </div>
    </div>

    <!-- Divider (draggable) -->
    <transition name="divider-fade">
      <div
        v-if="showRight"
        ref="divider"
        class="w-1 bg-gray-300 cursor-col-resize hover:bg-gray-400 active:bg-gray-500 z-10"
        @mousedown="startDrag"
      ></div>
    </transition>

    <!-- Right pane (slides in/out) -->
    <transition name="slide-right">
      <div
        v-if="showRight"
        class="flex-1 overflow-hidden h-full transition-all duration-300 ease-in-out"
      >
        <div ref="rightWrapper" class="h-full">
          <slot name="right" />
        </div>
      </div>
    </transition>

    <!-- External toggle slot -->
    <slot name="toggle" :toggleRight="toggleRight" :showRight="showRight" />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useResizeObserver } from "@vueuse/core";
import { defineExpose } from "vue";

const splitContainer = ref<HTMLElement | null>(null);
const leftWrapper = ref<HTMLElement | null>(null);
const rightWrapper = ref<HTMLElement | null>(null);

const showRight = ref(false);
const leftWidth = ref(0);

let leftScroll: HTMLElement | null = null;
let rightScroll: HTMLElement | null = null;
let isDragging = false;
let isSyncing = false;

// --- Drag logic ---
const startDrag = (e: MouseEvent) => {
  isDragging = true;
  document.body.style.cursor = "col-resize";
  document.addEventListener("mousemove", onDrag);
  document.addEventListener("mouseup", stopDrag);
};

const onDrag = (e: MouseEvent) => {
  if (!isDragging || !splitContainer.value) return;
  const rect = splitContainer.value.getBoundingClientRect();
  const min = 150;
  const max = rect.width - 150;
  leftWidth.value = Math.min(Math.max(e.clientX - rect.left, min), max);
};

const stopDrag = () => {
  isDragging = false;
  document.body.style.cursor = "default";
  document.removeEventListener("mousemove", onDrag);
  document.removeEventListener("mouseup", stopDrag);
};

// --- Scroll sync ---
const handleLeftScroll = () => {
  if (isSyncing || !leftScroll || !rightScroll) return;
  isSyncing = true;
  rightScroll.scrollTop = leftScroll.scrollTop;
  requestAnimationFrame(() => (isSyncing = false));
};

const handleRightScroll = () => {
  if (isSyncing || !leftScroll || !rightScroll) return;
  isSyncing = true;
  leftScroll.scrollTop = rightScroll.scrollTop;
  requestAnimationFrame(() => (isSyncing = false));
};

const findScrollableChild = (root: HTMLElement | null): HTMLElement | null => {
  if (!root) return null;
  const els = root.querySelectorAll("*");
  for (const el of els) {
    const style = getComputedStyle(el);
    if (style.overflowY === "auto" || style.overflowY === "scroll") {
      return el as HTMLElement;
    }
  }
  return null;
};

// --- Toggle right pane ---
const toggleRight = async (keepOpen = false) => {
  showRight.value = keepOpen || !showRight.value;
  await nextTick();

  if (splitContainer.value) {
    const fullWidth = splitContainer.value.clientWidth;
    leftWidth.value = showRight.value ? fullWidth * 0.7 : fullWidth;
  }

  setupScrollSync();
};

// --- Setup scroll sync ---
const setupScrollSync = async () => {
  await nextTick();
  leftScroll = findScrollableChild(leftWrapper.value);
  rightScroll = findScrollableChild(rightWrapper.value);

  if (leftScroll) {
    leftScroll.removeEventListener("scroll", handleLeftScroll);
    leftScroll.addEventListener("scroll", handleLeftScroll);
  }

  if (rightScroll) {
    rightScroll.removeEventListener("scroll", handleRightScroll);
    rightScroll.addEventListener("scroll", handleRightScroll);
  }
};

defineExpose({
  toggleRight,
  showRight
});

// --- Initialize ---
onMounted(() => {
  if (splitContainer.value) leftWidth.value = splitContainer.value.clientWidth;
  setupScrollSync();
});

useResizeObserver(splitContainer, (entries) => {
  if (isDragging) return;
  const newWidth = entries[0].contentRect.width;
  if (showRight.value) {
    leftWidth.value = Math.min(Math.max(leftWidth.value, 150), newWidth - 150);
  } else {
    leftWidth.value = newWidth;
  }
});

onBeforeUnmount(() => {
  if (leftScroll) leftScroll.removeEventListener("scroll", handleLeftScroll);
  if (rightScroll) rightScroll.removeEventListener("scroll", handleRightScroll);
});
</script>

<style scoped>
* {
  user-select: none;
}

/* Slide animation for right pane */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.3s ease;
}
.slide-right-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Divider fade animation */
.divider-fade-enter-active,
.divider-fade-leave-active {
  transition: opacity 0.2s ease;
}
.divider-fade-enter-from,
.divider-fade-leave-to {
  opacity: 0;
}

</style>
